import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { FileText, Sparkles, ArrowLeft, Check, Pencil, Send, X, Loader } from 'lucide-react';
import { usePrototype2 } from '../Prototype2Context';
import OutlineSectionsPanel from '../OutlineSectionsPanel';
import type { OutlineSection } from '../prototype2Data';
const REVIEW_EXCHANGE_LENGTH = 2;

const AssistantTypingIndicator = ({ label }: { label: string }) => (
  <div className="flex items-start gap-2">
    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#171717] shrink-0 mt-0.5">
      <Sparkles className="w-3 h-3 text-white" />
    </div>
    <div className="flex items-center gap-2 pt-0.5">
      <Loader className="w-3.5 h-3.5 text-[#737373] animate-spin shrink-0" />
      <span className="text-xs text-[#737373]">{label}</span>
    </div>
  </div>
);

const Screen02ReviewOutline = () => {
  const {
    chatHistory,
    isChatPending,
    sendChat,
    goToScreen,
    returnToNarrative,
    beginParagraphDraft,
    approveOutlineForDrafting,
    outlineSections,
  } = usePrototype2();

  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestInput, setSuggestInput] = useState('');
  const suggestRef = useRef<HTMLTextAreaElement>(null);
  const initialOutlineSnapshotRef = useRef<OutlineSection[] | null>(null);

  const narrativeChoiceMessage = chatHistory.find((m) => m.role === 'user');
  const outlineFollowUpMessages = chatHistory.slice(REVIEW_EXCHANGE_LENGTH);
  const isInitialOutlineReviewPending = isChatPending && chatHistory.length === 1;
  const isOutlineActionsBlocked = isChatPending;

  useEffect(() => {
    if (!isInitialOutlineReviewPending && initialOutlineSnapshotRef.current === null) {
      initialOutlineSnapshotRef.current = outlineSections;
    }
  }, [isInitialOutlineReviewPending, outlineSections]);

  const initialOutlineSections = initialOutlineSnapshotRef.current ?? outlineSections;

  const handleApprove = async () => {
    if (isOutlineActionsBlocked) return;

    const sections = approveOutlineForDrafting();
    const firstSection = sections[0];
    const firstParagraph = firstSection?.paragraphs[0];
    if (!firstSection || !firstParagraph) return;

    goToScreen('03-draft-report');
    beginParagraphDraft(firstParagraph.id);
    await sendChat(`Approved — please draft the first paragraph of ${firstSection.title} (${firstParagraph.title}).`, {
      intent: 'draft',
      screen_id: '03-draft-report',
      active_section_id: firstParagraph.id,
      current_draft: '',
      ui_state: { last_user_action: 'approve-outline' },
      response_hint: { shape: 'draft-paragraph' },
    });
  };

  const handleToggleSuggest = () => {
    setSuggestOpen((prev) => !prev);
    setSuggestInput('');
    setTimeout(() => suggestRef.current?.focus(), 0);
  };

  const handleSuggestSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    const trimmed = suggestInput.trim();
    if (!trimmed || isChatPending) return;
    setSuggestInput('');
    setSuggestOpen(false);
    await sendChat(trimmed, {
      intent: 'qa',
      ui_state: { last_user_action: 'suggest-outline-changes' },
      response_hint: { shape: 'outline-revision' },
    });
  };

  const handleSuggestKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSuggestSubmit();
    }
  };

  return (
    <div className="flex h-full w-full bg-[#fafafa] font-['Inter']">
      <div className="flex flex-col flex-1 min-w-0">
        <header className="flex items-center gap-3 px-6 h-14 border-b border-[#e5e5e5] bg-[#fafafa] shrink-0">
          <FileText className="w-5 h-5 text-[#0a0a0a]" />
          <span className="text-[15px] font-semibold text-[#0a0a0a]">EOAA Report Builder</span>
          <span className="text-sm text-[#737373]">|</span>
          <span className="text-sm text-[#737373]">Issaquah School District - 2023-24</span>
          <div className="flex-1" />
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-[#f5f5f5] text-[#737373] border border-[#e5e5e5]">
            Step 2 of 3: Review Outline
          </span>
        </header>

        <div className="flex flex-col flex-1 items-center py-10 px-6 overflow-y-auto">
          <div className="flex flex-col gap-8 w-full max-w-[720px]">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#171717] shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-semibold text-[#0a0a0a]">AI Draft Assistant</span>
                <span className="text-xs text-[#737373]">Just now</span>
              </div>
              <div className="pl-[42px]">
                <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                  Great choices! Based on your selected narrative direction, uploaded materials, and additional context,
                  I've put together a proposed outline for the Issaquah School District EOAA report. Here's the info I
                  have from you:
                </p>
              </div>
            </div>

            {narrativeChoiceMessage && (
              <div className="flex justify-center">
                <div className="w-[560px] rounded-xl bg-[#f5f5f5] p-4">
                  <p className="text-sm text-[#0a0a0a] leading-[1.6]">{narrativeChoiceMessage.content}</p>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#171717] shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-semibold text-[#0a0a0a]">AI Draft Assistant</span>
                <span className="text-xs text-[#737373]">Just now</span>
              </div>
              <div className="flex flex-col gap-4 pl-[42px]">
                {isInitialOutlineReviewPending ? (
                  <AssistantTypingIndicator label="Preparing your outline review…" />
                ) : (
                  <>
                    <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                      Here's the proposed outline for the Issaquah School District 2023-24 EOAA Report:
                    </p>

                    <OutlineSectionsPanel sections={initialOutlineSections} />

                    <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                      Does this outline capture what you're looking for? I can add, remove, or reorganize sections
                      before we start drafting. You can also adjust the emphasis on any particular area.
                    </p>
                  </>
                )}

                {outlineFollowUpMessages.length > 0 && (
                  <div className="flex flex-col gap-3">
                    {outlineFollowUpMessages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'items-start gap-2'}`}>
                        {m.role === 'assistant' && (
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#171717] shrink-0 mt-0.5">
                            <Sparkles className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {m.role === 'user' ? (
                          <div className="rounded-lg px-3 py-2.5 bg-[#171717] text-white max-w-[80%]">
                            <p className="text-[13px] leading-[1.5]">{m.content}</p>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-3 flex-1 min-w-0">
                            {m.content.trim() && <p className="text-sm text-[#0a0a0a] leading-[1.6]">{m.content}</p>}
                            {m.outlineSections && m.outlineSections.length > 0 && (
                              <OutlineSectionsPanel sections={m.outlineSections} />
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {!isInitialOutlineReviewPending && isChatPending && <AssistantTypingIndicator label="AI is typing…" />}

                {suggestOpen && (
                  <form onSubmit={handleSuggestSubmit} className="flex flex-col gap-2">
                    <textarea
                      ref={suggestRef}
                      value={suggestInput}
                      onChange={(e) => setSuggestInput(e.target.value)}
                      onKeyDown={handleSuggestKeyDown}
                      placeholder="Describe your suggested changes to the outline..."
                      rows={3}
                      className="w-full px-3 py-2.5 text-[13px] text-[#0a0a0a] placeholder:text-[#737373] rounded-md bg-[#fafafa] border border-[#e5e5e5] outline-none focus:border-[#a3a3a3] resize-none transition-colors"
                    />
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-[12px] text-[#737373]">Enter to send · Shift+Enter for new line</span>
                      <button
                        type="submit"
                        disabled={!suggestInput.trim() || isChatPending}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#171717] text-white text-[13px] font-medium hover:bg-[#2a2a2a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Send className="w-3.5 h-3.5" />
                        Send
                      </button>
                    </div>
                  </form>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={returnToNarrative}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[#0a0a0a] font-medium hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Narrative
                  </button>
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={isOutlineActionsBlocked}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#171717] text-white text-sm font-medium hover:bg-[#2a2a2a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Check className="w-4 h-4" />
                    Approve & Start Drafting
                  </button>
                  <button
                    type="button"
                    onClick={handleToggleSuggest}
                    disabled={isOutlineActionsBlocked}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-white border border-[#e5e5e5] text-[#0a0a0a] text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {suggestOpen ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                    {suggestOpen ? 'Cancel' : 'Suggest Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Screen02ReviewOutline;
